import { useCallback, useEffect, useContext, useRef, useState } from 'react';

import { getCurrentUser } from '../api';
import { CentrifugoContext } from '../context-providers/CentrifugoContext';

type AIAgentResponseHandler = (threadId: number, error?: string | null) => void;

/**
 * Hook for managing Centrifugo subscription for AI Agent responses.
 * Handles staff ID loading and subscribes to the staff channel for real-time notifications.
 */
export const useAIAgentCentrifugo = (onAgentResponse: AIAgentResponseHandler) => {
  const [staffId, setStaffId] = useState<number | null>(null);
  const centrifugo = useContext(CentrifugoContext);

  // Use ref to always have the latest callback without re-subscribing
  const onAgentResponseRef = useRef(onAgentResponse);
  useEffect(() => {
    onAgentResponseRef.current = onAgentResponse;
  }, [onAgentResponse]);

  // Load current user on mount
  useEffect(() => {
    const loadUser = async() => {
      try {
        const user = await getCurrentUser();
        setStaffId(user.id);
        console.info('[AIAgent] Loaded staff ID:', user.id);
      } catch (err) {
        console.error('[AIAgent] Error loading current user:', err);
      }
    };

    loadUser();
  }, []);

  // Stable handler that uses ref
  const handleAgentResponse = useCallback((threadId: number, error?: string | null) => {
    onAgentResponseRef.current(threadId, error);
  }, []);

  // Subscribe to Centrifugo staff channel when staffId is available
  useEffect(() => {
    if (!centrifugo || !staffId) {
      return;
    }

    console.info('[AIAgent] Subscribing to Centrifugo staff channel:', staffId);
    const unsubscribe = centrifugo.subscribeToStaffChannel(staffId, handleAgentResponse);

    return () => {
      console.info('[AIAgent] Unsubscribing from Centrifugo staff channel');
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [centrifugo, staffId, handleAgentResponse]);

  return { staffId };
};
